import { targetKey } from '@/lib/notes-queries';
import type {
  Cell,
  Dimension,
  NoteTarget,
  NoteThread,
  PalierNumber,
  ResolvedPalier,
  SingularityLevier,
} from '@mss_creator/shared';
import { resolveColumnLabel, resolveRowLabel } from './matrix-labels';
import { MATRIX_SELECTION, type Selection } from './selection';

/**
 * The bridge between the grid's `Selection` and the targets of the notes
 * (issue #19, `docs/specs/matrices.md` « Notes de matrice »): which spot a
 * selection carries notes for, which selection a note's spot corresponds to,
 * how a spot is worded, and the per-spot figures the markers show.
 * Pure, so it is tested on its own.
 */

/** What the labels of a target are resolved against. */
export interface NoteTargetContext {
  singularitySetup: SingularityLevier[];
  dimensions: Dimension[];
  paliers: ResolvedPalier[];
}

/**
 * The spot a selection writes notes on: the matrix when nothing is selected, a
 * cell, a row or a column otherwise. A multiple selection and the activation
 * block carry no note (`null`), as the spec says.
 */
export function selectionToNoteTarget(selection: Selection, cells: Cell[]): NoteTarget | null {
  switch (selection.scope) {
    case 'matrix':
      return { type: 'matrix' };
    case 'column':
      return { type: 'column', columnNumber: selection.columnNumber as PalierNumber };
    case 'row':
      return { type: 'row', rowType: selection.rowType, rowRef: selection.rowRef };
    case 'cell': {
      const cell = cells.find((entry) => entry.id === selection.cellId);
      return cell
        ? {
            type: 'cell',
            rowType: cell.rowType,
            rowRef: cell.rowRef,
            columnNumber: cell.columnNumber as PalierNumber,
          }
        : null;
    }
    case 'cells':
    case 'activationBlock':
      return null;
  }
}

/** The selection that lands on a note's spot, or `null` for a folder or a cell that is gone. */
export function noteTargetToSelection(target: NoteTarget, cells: Cell[]): Selection | null {
  switch (target.type) {
    case 'matrix':
      return MATRIX_SELECTION;
    case 'column':
      return { scope: 'column', columnNumber: target.columnNumber };
    case 'row':
      return { scope: 'row', rowType: target.rowType, rowRef: target.rowRef };
    case 'cell': {
      const cell = cells.find(
        (entry) =>
          entry.rowType === target.rowType &&
          entry.rowRef === target.rowRef &&
          entry.columnNumber === target.columnNumber,
      );
      return cell ? { scope: 'cell', cellId: cell.id } : null;
    }
    case 'folder':
      return null;
  }
}

/**
 * How a spot is worded (the spec's table): « Matrice », « <ligne> × <palier> »,
 * « Ligne <libellé> », « Palier <libellé> ».
 */
export function noteTargetLabel(target: NoteTarget, context: NoteTargetContext): string {
  const { singularitySetup, dimensions, paliers } = context;
  switch (target.type) {
    case 'matrix':
      return 'Matrice';
    case 'folder':
      return 'Dossier';
    case 'column':
      return `Palier ${resolveColumnLabel(target.columnNumber, paliers)}`;
    case 'row':
      return `Ligne ${resolveRowLabel(target.rowType, target.rowRef, singularitySetup, dimensions)}`;
    case 'cell':
      return `${resolveRowLabel(target.rowType, target.rowRef, singularitySetup, dimensions)} × ${resolveColumnLabel(target.columnNumber, paliers)}`;
  }
}

/** The latest open discussion of each spot (by last activity): what the marker tooltips preview. */
export function latestOpenByTarget(threads: NoteThread[]): Map<string, NoteThread> {
  const latest = new Map<string, NoteThread>();
  for (const thread of threads) {
    if (thread.resolvedAt !== null) continue;
    const key = targetKey(thread.target);
    const current = latest.get(key);
    if (!current || thread.lastActivityAt > current.lastActivityAt) latest.set(key, thread);
  }
  return latest;
}

/** The DOM attribute the grid puts on every spot a note can be anchored to. */
export const NOTE_TARGET_ATTRIBUTE = 'data-note-target';

/** Total of open discussions of a matrix (the toolbar's « Notes » counter). */
export function totalOpenCount(counts: ReadonlyMap<string, number>): number {
  let total = 0;
  for (const count of counts.values()) total += count;
  return total;
}

/** A request to show the notes of a spot: from a marker (read), the « + » bubble or the « ⋯ » menu (compose). */
export interface NotesRequest {
  target: NoteTarget;
  /** The cell or header the notes belong to: where the bubble anchors. */
  anchor: HTMLElement;
  /** Open on the field that writes a new note rather than on the discussions. */
  compose: boolean;
}

/**
 * What the grid needs to draw the notes (issue #19): the open discussions per
 * spot, the latest of each for the preview, whether the markers are shown at
 * all, and where to send a request. Absent, the grid has no notes at all.
 */
export interface GridNotes {
  counts: ReadonlyMap<string, number>;
  latest: ReadonlyMap<string, NoteThread>;
  /** False once the viewer hid the markers: no marker, no « + » bubble, no count in the preview. */
  markersVisible: boolean;
  onRequest: (request: NotesRequest) => void;
  /** The clock of the relative dates of the previews; tests pin it. */
  now?: Date;
}

/**
 * The cell or header a note target is drawn on inside `root`, found by the
 * `data-note-target` the grid puts on it. An activation row has no header of
 * its own, so its first cell stands in; the matrix, a hidden column or a hidden
 * row have none (`null`).
 */
export function findNoteAnchor(root: HTMLElement | null, target: NoteTarget): HTMLElement | null {
  if (!root || target.type === 'matrix' || target.type === 'folder') return null;
  const exact = root.querySelector<HTMLElement>(
    `[${NOTE_TARGET_ATTRIBUTE}="${targetKey(target)}"]`,
  );
  if (exact) return exact;
  if (target.type === 'row' && target.rowType === 'activation') {
    return root.querySelector<HTMLElement>(
      `[${NOTE_TARGET_ATTRIBUTE}^="cell:activation:${target.rowRef}:"]`,
    );
  }
  return null;
}
